import React, { useState, useEffect } from 'react';
import { closingAPI } from 'services/apisService';
import { updateLeadToClosedWon, updateLeadToPilotPlan } from 'services/apisService';

const ClosedWonModal = ({ isOpen, onClose, onSubmit, leadData, isFromKPITable, mode = 'closed_won', defaultAmount = '' }) => {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount || '');
      setComment('');
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, defaultAmount]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!amount || amount.trim() === '') {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(amount) || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please enter a valid positive amount';
    }
    
    if (!comment || comment.trim() === '') {
      newErrors.comment = 'Comment is required';
    } else if (comment.trim().length < 10) {
      newErrors.comment = 'Comment must be at least 10 characters long';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }

  setIsSubmitting(true);
  
  try {
    if (isFromKPITable) {
      await closingAPI.createClosingLead({
        amount: parseFloat(amount),
        comment: comment.trim(),
        contact_id: leadData?.leadContactId
      }, leadData?.userId);
    } else {
      if (mode === 'pilot_plan') {
        await updateLeadToPilotPlan(leadData?.id, {
          amount: parseFloat(amount),
          comment: comment.trim(),
          contact_id: leadData?.leadContactId
        });
      } else {
        await updateLeadToClosedWon(leadData?.id, {
          amount: parseFloat(amount),
          comment: comment.trim(),
          contact_id: leadData?.leadContactId
        });
      }
    }

    if (onSubmit) {
      onSubmit({
        amount: parseFloat(amount),
        comment: comment.trim(),
        leadId: leadData?.leadContactId,
        status: mode === 'pilot_plan' ? 'pilot_plan' : 'closed_won'
      });
    }
    
  } catch (error) {
    console.error('Error updating lead status:', error);
    setErrors({
      submit: error.message || 'Failed to update lead status. Please try again.'
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleCancel = () => {
    setAmount('');
    setComment('');
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const formatAmount = (value) => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    return numericValue;
  };

  const handleAmountChange = (e) => {
    const formattedValue = formatAmount(e.target.value);
    setAmount(formattedValue);
    
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    
    if (errors.comment) {
      setErrors(prev => ({ ...prev, comment: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#000] bg-opacity-50 backdrop-blur-[15px] flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4898cd] to-[#3985b6] text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {mode === 'pilot_plan' ? 'Move to Pilot Plan 🚀' : 'Deal Closed Won! 🎉'}
                </h2>
                <p className="text-green-100 text-sm mt-1">
                  {leadData?.name || 'Lead'} - {leadData?.companyName || 'Company'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Submit Error */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.submit}
                </p>
              </div>
            )}

            <div className="space-y-6">
              {/* Amount Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Deal Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                   <span className="text-gray-500 text-lg font-medium">₹</span>
                  </div>
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    disabled={isSubmitting}
                    className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                      errors.amount 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                        : 'border-gray-300 focus:border-[#3985b6] focus:ring-[#3985b6]'
                    } ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>
                {errors.amount && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.amount}
                  </p>
                )}
              </div>

              {/* Comment Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {mode === 'pilot_plan' ? 'Comments' : 'Closing Comments'} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder={mode === 'pilot_plan' ? 'Enter details about the pilot plan, terms, or any important notes...' : 'Enter details about the deal closure, terms, or any important notes...'}
                  rows={4}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none ${
                    errors.comment 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-300 focus:border-[#3985b6] focus:ring-[#3985b6]'
                  } ${isSubmitting ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                />
                <div className="flex justify-between items-center mt-2">
                  {errors.comment ? (
                    <p className="text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.comment}
                    </p>
                  ) : (
                    <span className="text-sm text-gray-500">
                      Minimum 10 characters required
                    </span>
                  )}
                  <span className="text-sm text-gray-400">
                    {comment.length}/500
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-8">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                className={`flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors font-medium ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-4 py-3 bg-gradient-to-r from-[#4898cd] to-[#3985b6] text-white rounded-lg hover:from-[#3a6f93] hover:to-[#55a7dd] focus:outline-none focus:ring-2 focus:ring-green-200 transition-all font-medium shadow-md hover:shadow-lg ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  mode === 'pilot_plan' ? 'Confirm Pilot Plan' : 'Confirm Deal'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClosedWonModal;